import {
  useDateTimeFormat,
  useInterval,
} from '@issuemanager/apps/client/hooks';
import { useEventCallback } from '@mui/material';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';

const DELAY = 60000;
const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

interface Params {
  createdAt: string;
  slaDays: number | null;
}

export function useCounter({ createdAt, slaDays }: Params) {
  const { timezoneOffset } = useDateTimeFormat();

  const [daysLeft, setDaysLeft] = useState(0);
  const [hoursLeft, setHoursLeft] = useState(0);
  const [minutesLeft, setMinutesLeft] = useState(0);

  const calculateTimeLeft = useEventCallback(() => {
    const noTimeLeft = {
      days: 0,
      hours: 0,
      minutes: 0,
    };

    if (!slaDays) {
      return noTimeLeft;
    }

    const createdAtDate = dayjs.utc(createdAt).utcOffset(timezoneOffset);
    const expirationDate = createdAtDate.add(slaDays, 'day');
    const currentDate = dayjs();
    const duration = expirationDate.diff(currentDate);

    if (duration <= 0) {
      return noTimeLeft;
    }

    const days = Math.floor(duration / DAY);
    const hours = Math.floor((duration % DAY) / HOUR);
    const minutes = Math.floor((duration % HOUR) / MINUTE);

    return {
      days,
      hours,
      minutes,
    };
  });

  const refreshTimeLeft = useEventCallback(() => {
    const values = calculateTimeLeft();
    setDaysLeft(values.days);
    setHoursLeft(values.hours);
    setMinutesLeft(values.minutes);
  });

  const { initiateInterval, killInterval } = useInterval({
    fn: refreshTimeLeft,
    delay: DELAY,
  });

  useEffect(() => {
    refreshTimeLeft();
    initiateInterval();

    return () => killInterval();
  }, [initiateInterval, killInterval, refreshTimeLeft]);

  return {
    daysLeft,
    hoursLeft,
    minutesLeft,
    initiateInterval,
    killInterval,
    refreshTimeLeft,
  };
}
